/**
 * Estimate sharing — public client for /api/share.php (docs/ESTIMATE-SHARING-PLAN.md).
 *
 * Two write paths, like comments: a logged-in owner proves intent with the CSRF
 * token (csrfFetch), a guest owner with Turnstile + the honeypot. Both send the
 * ORIGINAL guid — the server refuses child guids here.
 */

import { csrfFetch } from './api';
import type { InboxItem, InboxLocation, ShareActionResult, ShareLocationInput, ShareSendResult, SharesResponse } from '../types/share';

const API_URL = process.env.REACT_APP_API_URL || '';

async function parse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((data as { error?: string }).error || fallback) as Error & { status?: number; field?: string; retryAfter?: number };
    err.status = res.status;
    if ((data as { field?: string }).field) err.field = (data as { field?: string }).field;
    if ((data as { retryAfter?: number }).retryAfter) err.retryAfter = (data as { retryAfter?: number }).retryAfter;
    throw err;
  }
  return data as T;
}

async function post(url: string, body: unknown, authed: boolean): Promise<Response> {
  if (authed) return csrfFetch(url, { jsonBody: body });
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** The owner's shares for an ORIGINAL guid. */
export async function fetchShares(guid: string): Promise<SharesResponse> {
  const res = await fetch(`${API_URL}/api/share.php?guid=${encodeURIComponent(guid)}`, { credentials: 'include' });
  return parse<SharesResponse>(res, 'Failed to load shares');
}

export interface CreateSharesInput {
  guid: string;
  senderName: string;
  senderEmail: string;
  locations: ShareLocationInput[];
  message?: string;
  locale?: string;
  turnstileToken?: string;
  /** Honeypot — must stay empty. */
  website?: string;
}

export async function createShares(input: CreateSharesInput, authed: boolean): Promise<ShareSendResult[]> {
  const res = await post(`${API_URL}/api/share.php`, input, authed);
  const data = await parse<{ results: ShareSendResult[] }>(res, 'Failed to send');
  return data.results ?? [];
}

async function action(name: 'resend' | 'remove' | 'promote' | 'demote', body: Record<string, unknown>, authed: boolean): Promise<ShareActionResult> {
  const res = await post(`${API_URL}/api/share.php?action=${name}`, body, authed);
  return parse<ShareActionResult>(res, 'Request failed');
}

export function resendShare(guid: string, shareId: number, authed: boolean, turnstileToken?: string): Promise<ShareActionResult> {
  return action('resend', { guid, shareId, turnstileToken }, authed);
}

export function removeShare(guid: string, shareId: number, authed: boolean, turnstileToken?: string): Promise<ShareActionResult> {
  return action('remove', { guid, shareId, turnstileToken }, authed);
}

export function promoteShare(guid: string, shareId: number, targets: 'all' | number[], authed: boolean, turnstileToken?: string): Promise<ShareActionResult> {
  return action('promote', { guid, shareId, targets, turnstileToken }, authed);
}

export function demoteShare(guid: string, shareId: number, authed: boolean, turnstileToken?: string): Promise<ShareActionResult> {
  return action('demote', { guid, shareId, turnstileToken }, authed);
}

/** Estimates shared with the logged-in user's partner location(s). */
export async function fetchPartnerInbox(): Promise<{ locations: InboxLocation[]; shares: InboxItem[] }> {
  const res = await fetch(`${API_URL}/api/auth-partner-inbox.php`, { credentials: 'include' });
  return parse(res, 'Failed to load shared estimates');
}
